import React from 'react';
import { ChevronLeft, ChevronRight, Save, Check } from 'lucide-react';
import { ApprovalButton } from '../shared/approval';

interface WizardFooterStickyProps {
  currentStep: number;
  totalSteps: number;
  canProceed: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onSaveDraft: () => void;
  onComplete: () => void;
  onCompleteAndAddAnother?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
}

export const WizardFooterSticky: React.FC<WizardFooterStickyProps> = ({
  currentStep,
  totalSteps,
  canProceed,
  onPrevious,
  onNext,
  onSaveDraft,
  onComplete,
  onCompleteAndAddAnother,
  onCancel,
  isSaving = false,
}) => {
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;
  const isOptionalStep = currentStep >= 4; // Steps 4 and 5 are optional

  return (
    <div className="sticky bottom-0 z-10 border-t border-border bg-card/95 backdrop-blur-sm shadow-lg px-4 py-2.5 transition-all duration-300">
      {/* Keyboard Shortcuts Hint */}
      <div className="hidden lg:flex items-center justify-center gap-3 text-[10px] text-muted-foreground mb-1.5 pb-1.5 border-b border-border/50">
        <span className="flex items-center gap-1">
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[9px] font-mono">ESC</kbd>
          <span>Cancel</span>
        </span>
        <span className="text-muted-foreground/50">•</span>
        <span className="flex items-center gap-1">
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[9px] font-mono">Ctrl</kbd>
          <span>+</span>
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[9px] font-mono">S</kbd>
          <span>Save Draft</span>
        </span>
        {isLastStep && (
          <>
            <span className="text-muted-foreground/50">•</span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-[9px] font-mono">Ctrl</kbd>
              <span>+</span>
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-[9px] font-mono">Enter</kbd>
              <span>Complete</span>
            </span>
          </>
        )}
        <span className="text-muted-foreground/50">•</span>
        <span className="flex items-center gap-1">
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[9px] font-mono">←</kbd>
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[9px] font-mono">→</kbd>
          <span>Navigate</span>
        </span>
      </div>
      
      <div className="flex items-center justify-between max-w-5xl mx-auto">
        {/* Left: Cancel and Previous Buttons */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {onCancel && (
            <ApprovalButton
              variant="ghost"
              onClick={onCancel}
              size="sm"
              className="h-8 px-3 text-xs transition-all duration-200 hover:scale-105"
            >
              Cancel
            </ApprovalButton>
          )}
          <ApprovalButton
            variant="outline"
            onClick={onPrevious}
            disabled={isFirstStep}
            icon={<ChevronLeft className="w-3.5 h-3.5" />}
            iconPosition="left"
            size="sm"
            className="h-8 px-3 text-xs transition-all duration-200 hover:scale-105"
          >
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Prev</span>
          </ApprovalButton>
        </div>

        {/* Center: Step Indicator with Progress */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-xs text-muted-foreground font-medium">
            Step {currentStep} of {totalSteps}
          </div>
          <div className="hidden md:flex items-center gap-1">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
              <div
                key={step}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  step === currentStep
                    ? 'bg-primary w-4'
                    : step < currentStep
                    ? 'bg-primary/60'
                    : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Right: Save Draft and Next/Complete Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <ApprovalButton
            variant="ghost"
            onClick={onSaveDraft}
            icon={isSaving ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
            iconPosition="left"
            size="sm"
            disabled={isSaving}
            className="h-8 px-3 text-xs transition-all duration-200 hover:scale-105"
          >
            <span className="hidden sm:inline">{isSaving ? 'Saved' : 'Save Draft'}</span>
            <span className="sm:hidden">{isSaving ? 'Saved' : 'Save'}</span>
          </ApprovalButton>

          {isLastStep ? (
            <>
              {onCompleteAndAddAnother && (
                <ApprovalButton
                  variant="outline"
                  onClick={onCompleteAndAddAnother}
                  size="sm"
                  icon={<Check className="w-3.5 h-3.5" />}
                  iconPosition="left"
                  className="h-8 px-3 text-xs transition-all duration-200 hover:scale-105"
                >
                  <span className="hidden sm:inline">Save & Add Another</span>
                  <span className="sm:hidden">+ Another</span>
                </ApprovalButton>
              )}
              <ApprovalButton
                variant="success"
                onClick={onComplete}
                size="sm"
                icon={<Check className="w-3.5 h-3.5" />}
                iconPosition="right"
                className="h-8 px-3 text-xs transition-all duration-200 hover:scale-105"
              >
                <span className="hidden sm:inline">Complete Stage</span>
                <span className="sm:hidden">Complete</span>
              </ApprovalButton>
            </>
          ) : (
            <ApprovalButton
              variant="primary"
              onClick={onNext}
              disabled={!canProceed && !isOptionalStep}
              icon={<ChevronRight className="w-3.5 h-3.5" />}
              iconPosition="right"
              size="sm"
              className="h-8 px-3 text-xs transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="hidden sm:inline">Next Step</span>
              <span className="sm:hidden">Next</span>
            </ApprovalButton>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-muted">
        <div
          className="h-full bg-primary transition-all duration-500 ease-out"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );
};
